import { Link } from "react-router-dom";
import { buildUrl, useApi } from "../api";
import type { Counted, Party, ScoreRow, Stats } from "../api";
import {
  Bar,
  Card,
  ErrorNote,
  Loading,
  PartyTag,
  Tile,
  formatDate,
  formatNumber,
} from "../components";

interface Initiator extends Counted {
  initiator: string;
}

export default function Dashboard() {
  const stats = useApi<Stats>(buildUrl("/api/stats"));
  const parties = useApi<Party[]>(buildUrl("/api/parties"));
  const initiatoren = useApi<Initiator[]>(buildUrl("/api/initiatoren", { limit: 8 }));
  const score = useApi<ScoreRow[]>(buildUrl("/api/scoreboard"));

  const c = stats.data?.counts;
  const maxInit = Math.max(1, ...(initiatoren.data ?? []).map((i) => i.anzahl));

  return (
    <>
      <div className="page-head">
        <h1>Versprochen und getan</h1>
        <p>
          Was Parteien vor der Wahl zugesagt haben und was sie danach im Bundestag
          tatsächlich tun: welche Vorlagen sie einbringen und wie sie abstimmen.
          Aktuell erfasst: 21.&nbsp;Wahlperiode, 2025 bis 2029.
        </p>
      </div>

      {stats.error ? (
        <ErrorNote message={stats.error} onRetry={stats.reload} />
      ) : stats.loading ? (
        <Loading rows={3} />
      ) : c ? (
        <div className="grid tiles" style={{ marginBottom: "1rem" }}>
          <Tile
            label="Abstimmungen"
            value={formatNumber(c.abstimmungen)}
            note={`${formatNumber(c.abstimmungen_verknuepft)} mit Gesetzesvorgang`}
          />
          <Tile label="Einzelstimmen" value={formatNumber(c.einzelstimmen)} />
          <Tile label="Vorgänge" value={formatNumber(c.vorgaenge)} note="Gesetze und Anträge" />
          <Tile
            label="Versprechen"
            value={formatNumber(c.versprechen)}
            note={`aus ${formatNumber(c.programme)} Programmen`}
          />
          <Tile
            label="Bewertet"
            value={formatNumber(c.bewertungen)}
            note={`${formatNumber(c.belege)} Belege`}
          />
        </div>
      ) : null}

      {c && c.versprechen === 0 ? (
        <div className="notice" style={{ marginBottom: "1rem" }}>
          <strong>Noch keine Versprechen erfasst.</strong> Die Wahlprogramme sind
          geladen, aber noch nicht ausgewertet. Der Extraktionslauf braucht einen
          Anthropic-API-Schlüssel in der <code>.env</code>; danach{" "}
          <code>docker compose run --rm api politwatch promises</code>.
        </div>
      ) : null}

      <div className="grid grid-2">
        <Card
          title="Wer bringt Vorlagen ein?"
          subtitle="Anzahl eingebrachter Gesetzentwürfe und Anträge. Die Opposition kann nichts umsetzen — hier wird ihre Themensetzung sichtbar."
        >
          {initiatoren.error ? (
            <ErrorNote message={initiatoren.error} onRetry={initiatoren.reload} />
          ) : initiatoren.loading ? (
            <Loading rows={6} />
          ) : (
            (initiatoren.data ?? []).map((row) => (
              <Bar
                key={row.initiator}
                label={row.initiator.replace(/^Fraktion (der |)/, "")}
                title={row.initiator}
                value={row.anzahl}
                max={maxInit}
              />
            ))
          )}
        </Card>

        <Card
          title="Parteien"
          subtitle="Rolle in der laufenden Wahlperiode. Ob ein Versprechen umgesetzt werden konnte, hängt zuerst daran."
        >
          {parties.error ? (
            <ErrorNote message={parties.error} onRetry={parties.reload} />
          ) : parties.loading ? (
            <Loading rows={5} />
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Partei</th>
                    <th>Rolle</th>
                    <th className="num">Versprechen</th>
                  </tr>
                </thead>
                <tbody>
                  {(parties.data ?? [])
                    .filter((p) => p.rolle !== null)
                    .map((p) => (
                      <tr key={p.id}>
                        <td>
                          <PartyTag short={p.short_name} color={p.color} />
                        </td>
                        <td>
                          <span
                            className={`badge${p.rolle === "regierung" ? " role-regierung" : ""}`}
                          >
                            {p.rolle === "regierung" ? "Regierung" : "Opposition"}
                          </span>
                        </td>
                        <td className="num">{formatNumber(p.versprechen)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {(score.data ?? []).some((r) => r.bewertet > 0) ? (
        <Card
          title="Bilanz je Partei"
          subtitle="Regierung und Opposition getrennt: „erfüllt“ setzt Regierungsmacht voraus, „konsistent“ nicht."
        >
          <div className="table-wrap" style={{ border: "none" }}>
            <table>
              <thead>
                <tr>
                  <th>Partei</th>
                  <th>Rolle</th>
                  <th className="num">erfüllt</th>
                  <th className="num">teilweise</th>
                  <th className="num">gebrochen</th>
                  <th className="num">bewertet</th>
                </tr>
              </thead>
              <tbody>
                {(score.data ?? []).map((r) => (
                  <tr key={r.short_name}>
                    <td>
                      <PartyTag short={r.short_name} color={r.color} />
                    </td>
                    <td>
                      <span className="pill">
                        {r.rolle === "regierung" ? "Regierung" : "Opposition"}
                      </span>
                    </td>
                    <td className="num">{formatNumber(r.erfuellt)}</td>
                    <td className="num">{formatNumber(r.teilweise)}</td>
                    <td className="num">{formatNumber(r.gebrochen)}</td>
                    <td className="num">
                      {formatNumber(r.bewertet)} / {formatNumber(r.versprechen_gesamt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {stats.data?.letzter_lauf ? (
        <p style={{ color: "var(--ink-3)", fontSize: "0.82rem", marginTop: "1.25rem" }}>
          Letzter Datenabruf: {stats.data.letzter_lauf.source}
          {stats.data.letzter_lauf.scope ? ` (${stats.data.letzter_lauf.scope})` : ""} am{" "}
          {formatDate(stats.data.letzter_lauf.started_at)} — Status{" "}
          {stats.data.letzter_lauf.status}. <Link to="/abstimmungen">Abstimmungen ansehen</Link>
        </p>
      ) : null}
    </>
  );
}
